import { formatInTimeZone, notEmpty } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import { useEffect, useState } from "react";
import { graphql, readInlineData, useFragment } from "react-relay";

import { ArticleAcknowledgementGateOverlay } from "~/components/article/article-acknowledgement-gate-overlay";
import { trackingAtom } from "~/components/tracking";
import { sendOnePlusXEvent } from "~/components/tracking/one-plus-x";
import { acknowledgementGateArticleIds } from "~/data";
import type { hooksArticleAcknowledgementGateOverlayArticle$key } from "~/queries/__generated__/hooksArticleAcknowledgementGateOverlayArticle.graphql";
import type { hooksContentInterestArticle$key } from "~/queries/__generated__/hooksContentInterestArticle.graphql";
import type { hooksTrackPageViewArticle$key } from "~/queries/__generated__/hooksTrackPageViewArticle.graphql";

import { UrlSectionNameMap } from "./consts";

type Sentiment = Record<string, unknown> & {
  readability_school_level: string[];
  sentiment_category: string;
};

export const useArticleAcknowledgementGateOverlay = (
  article_: hooksArticleAcknowledgementGateOverlayArticle$key,
) => {
  const { entityId: currentArticleId } = useFragment(
    graphql`
      fragment hooksArticleAcknowledgementGateOverlayArticle on Article {
        entityId
      }
    `,
    article_,
  );

  const [acknowledgementAccepted, setAcknowledgementAccepted] = useState(false);
  const isAcknowledgementRequired =
    acknowledgementGateArticleIds.has(currentArticleId) && !acknowledgementAccepted;

  const renderAcknowledgementGateOverlay = () => (
    <>
      {isAcknowledgementRequired && (
        <ArticleAcknowledgementGateOverlay
          handleAcceptAcknowledgement={() => setAcknowledgementAccepted(true)}
        />
      )}
    </>
  );

  return {
    isAcknowledgementRequired,
    renderAcknowledgementGateOverlay,
  };
};

export const useTrackContentInterest = (article_: hooksContentInterestArticle$key | undefined) => {
  useEffect(() => {
    if (!notEmpty(article_)) return;

    const article = readInlineData(
      graphql`
        fragment hooksContentInterestArticle on Article @inline {
          entityId
          sentiment
          urlAlias
          types {
            value {
              name
            }
          }
          topics {
            name
          }
        }
      `,
      article_,
    );

    const [mainType] = first(article?.types ?? [])?.value ?? [];
    const contentType = `Article - ${mainType?.name ?? "Standard"}`;

    const sentiment = (article?.sentiment ?? {}) as Sentiment;
    const readabilitySchoolLevel = sentiment?.readability_school_level ?? [];
    const sentimentCategory = sentiment?.sentiment_category ?? "";
    const contentTopics =
      article?.topics?.map?.(topic => topic?.name ?? "").filter?.(name => notEmpty(name)) ?? [];

    const matchedPath =
      Object.keys(UrlSectionNameMap).find(path => article?.urlAlias?.startsWith?.(path)) ?? "";
    const sectionName = UrlSectionNameMap[matchedPath] ?? "";

    sendOnePlusXEvent({
      articleID: article.entityId,
      contentTopics: contentTopics?.join("|"),
      contentType,
      readability_school_level: readabilitySchoolLevel.join("|"),
      sentiment_category: sentimentCategory,
      siteVerticals: sectionName,
      type: "scmp-content-interests",
    });
  }, [article_]);
};

export const useTrackPageView = (article_: hooksTrackPageViewArticle$key) => {
  const article = useFragment(
    graphql`
      fragment hooksTrackPageViewArticle on Article {
        entityId
        publishedDate
        headline
        sections {
          value {
            name
          }
        }
        topics {
          name
          entityId
        }
        urlAlias
        authors {
          name
        }
        copyrighted
        entityUuid
        socialHeadline
        sponsor
      }
    `,
    article_,
  );

  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    const {
      authors,
      copyrighted,
      entityId,
      entityUuid,
      headline,
      publishedDate,
      sections,
      socialHeadline,
      sponsor,
      topics,
      urlAlias,
    } = article;

    const sanitizedAuthors = authors?.map(author => author?.name).join(", ");

    const joinedSectionNames = sections?.[0]?.value?.map(section => section?.name).join(", ");

    const sanitizedTopicData = topics?.reduce<{ id: string[]; name: string[] }>(
      (topicData, topic) => {
        if (topic?.entityId) topicData.id.push(topic.entityId);
        if (topic?.name) topicData.name.push(topic.name);
        return topicData;
      },
      {
        id: [],
        name: [],
      },
    );

    trackPageView({
      articleID: entityId,
      authorName: first(authors)?.name ?? "",
      authors: sanitizedAuthors,
      contentType: "Article",
      entityId,
      entityUuid,
      page: "Article",
      pageTitle: headline,
      path: urlAlias,
      primarySection: first(sections?.[0]?.value)?.name ?? "",
      publishedDate: publishedDate ? formatInTimeZone(publishedDate, "dd MMM yyyy") : null,
      scmpCopyright: copyrighted ? "1" : "0",
      sections: joinedSectionNames,
      socialHeadline,
      sponsor: sponsor ?? "",
      topicID: sanitizedTopicData?.id.join(", ") ?? "",
      topics: sanitizedTopicData?.name.join(", ") ?? "",
      webHeadline: headline ?? "",
    });
  }, [trackPageView, article]);
};
